import { StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, type DecorationSet } from '@codemirror/view';
import { headingAnchor, parseNote } from '@rbv/shared';

/** Brief line highlight used when navigating to a heading/block anchor. */

const setFlash = StateEffect.define<number | null>({
  map: (value, mapping) => (value == null ? value : mapping.mapPos(value)),
});

const flashLine = Decoration.line({ class: 'cm-flash' });

export const flashField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(deco, tr) {
    deco = deco.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setFlash)) {
        deco = e.value == null ? Decoration.none : Decoration.set([flashLine.range(e.value)]);
      }
    }
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

/** Locate a heading anchor or block id via the shared parser, scroll it into
 * view, and flash its line. */
export function scrollToTarget(
  view: EditorView,
  opts: { heading?: string; blockId?: string },
): void {
  const parse = parseNote(view.state.doc.toString());
  let pos: number | null = null;
  if (opts.blockId) {
    const b = parse.blocks.find((x) => x.id === opts.blockId);
    if (b) pos = b.start;
  } else if (opts.heading) {
    const want = headingAnchor(opts.heading).toLowerCase();
    const h = parse.headings.find((x) => x.anchor.toLowerCase() === want);
    if (h) pos = h.start;
  }
  if (pos == null || pos > view.state.doc.length) return;
  const lineFrom = view.state.doc.lineAt(pos).from;
  view.dispatch({
    effects: [
      EditorView.scrollIntoView(lineFrom, { y: 'start', yMargin: 60 }),
      setFlash.of(lineFrom),
    ],
    selection: { anchor: lineFrom },
  });
  window.setTimeout(() => {
    if (view.dom.isConnected) view.dispatch({ effects: setFlash.of(null) });
  }, 1600);
}
